import {useCallback, useEffect, useRef, useState} from 'react';
import {IoCaretDownSharp, IoCaretUpSharp} from 'react-icons/io5';
import styles from './table.module.css';

export default function Table({ table, dataType }) {
    const [initialPreviewTable, setInitialPreviewTable] = useState(table ? table.slice(0, 9) : null);
    const [sortedTable, setSortedTable] = useState(table);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null, isActive: false });
    const observerRef = useRef();

    // 테이블 헤더 설정하는 함수(. 기준으로 뒤에 있는 문자 반환)
    const formatHeader = (key) => {
        return key.split('.').pop();
    };

    // 구분자 붙인 숫자 문자열 생성하는 함수
    const formatNumberWithComma = (value) => {
        if (value === null) return 'NaN';

        const isFloat = Number(value) % 1 !== 0;                                  // 소수점을 가지는지 확인
        
        if (isFloat) {                                                            // 1. 소수점이 있는 경우 반올림하여 소수점 둘째 자리까지 표시
            const roundedValue = Math.round(Number(value) * 100) / 100;
            return roundedValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","); // 자릿수가 유지되도록 toFixed(2) 사용
        } else {                                                                  // 2. 소수점이 없는 경우(정수) 그대로 표시
            return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
    };

    // 정렬을 위한 숫자 판단 함수 (dataType을 기준으로 숫자 타입 확인)
    const isNumber = (key) => {
        const type = dataType[key];
        return type === 'INTEGER' || type === 'FLOAT';
    };

    const handleAsc = (key) => {
        if (initialPreviewTable) setInitialPreviewTable(null);
        handleSort(key, 'asc');
    };

    const handleDesc = (key) => {
        if (initialPreviewTable) setInitialPreviewTable(null);
        handleSort(key, 'desc');
    };

    // 정렬하는 함수
    const handleSort = (key, direction) => {
        const sortedData = [...sortedTable].sort((a, b) => {
            if (isNumber(key)) {
                const aValue = parseFloat(a[key]);
                const bValue = parseFloat(b[key]);
                return direction === 'asc' ? aValue - bValue : bValue - aValue;
            } else {
                const aValue = a[key] ? a[key].toString() : '';
                const bValue = b[key] ? b[key].toString() : '';
                return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }
        });
        setSortedTable(sortedData);
        setSortConfig({ key, direction, isActive: true });
    };

    const handleObserver = useCallback(
        (entries) => {
            const [entry] = entries;
            if (entry.isIntersecting && initialPreviewTable !== null) {
                setInitialPreviewTable(null);
            }
        },
        [initialPreviewTable]
    );

    useEffect(() => {
        if (observerRef.current) {
            const observer = new IntersectionObserver(handleObserver, {
                root: null,
                threshold: 0.1, //when 10% of the target is visible
            });
            observer.observe(observerRef.current);

            return () => observer.disconnect();
        }
    }, [handleObserver]);

    return (
        <div className={styles.tableContainer}>
            {initialPreviewTable !== null && initialPreviewTable.length > 0 &&
                <>
                    <table className={styles.table}>
                        <thead className={styles.thead}>
                        <tr>
                            {Object.keys(initialPreviewTable[0]).map((key) => (
                                <th className={styles.th}
                                    key={`h-tr-${key}`}
                                >
                                    <div className={styles.thContent}>
                                        <span>{formatHeader(key)}</span>
                                        <div className={styles.sortIcons}>
                                            <IoCaretUpSharp onClick={() => handleAsc(key)} className={`${styles.icon} ${sortConfig.isActive && sortConfig.key === key && sortConfig.direction === 'asc' ? styles.activeIcon : ''}`}  />
                                            <IoCaretDownSharp onClick={() => handleDesc(key)} className={`${styles.icon} ${sortConfig.isActive && sortConfig.key === key && sortConfig.direction === 'desc' ? styles.activeIcon : ''}`} />
                                        </div>
                                    </div>
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody className={styles.tbody}>
                        {initialPreviewTable.map((row, rowIndex) => (
                            <tr key={`b-tr-${rowIndex}`} className={styles.tr}>
                                {Object.entries(row).map(([key, value], colIndex) => (
                                    <td key={colIndex}
                                        className={styles.td}
                                        style={{ textAlign: isNumber(key) ? 'right' : 'left', borderBottomLeftRadius: rowIndex === initialPreviewTable.length - 1 && colIndex === 0 ? '10px' : '0', borderBottomRightRadius: rowIndex === initialPreviewTable.length - 1 && colIndex === Object.values(row).length - 1 ? '10px' : '0' }}>
                                        {isNumber(key) ? formatNumberWithComma(value) : value}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {initialPreviewTable &&
                            <div className={styles.observerRef} ref={observerRef}></div>
                        }
                        </tbody>
                    </table>
                    <div className={styles.viewMoreDiv} onClick={e => setInitialPreviewTable(null)}>
                        <span>더 보기</span>
                    </div>
                </>
            }
            {initialPreviewTable === null && sortedTable && sortedTable.length > 0 &&
                <table className={styles.table}>
                    <thead className={styles.thead}>
                    <tr>
                        {Object.keys(sortedTable[0]).map((key) => (
                            <th className={styles.th}
                                key={`h-tr-${key}`}
                            >
                                <div className={styles.thContent}>
                                    <span>{formatHeader(key)}</span>
                                    <div className={styles.sortIcons}>
                                        <IoCaretUpSharp onClick={() => handleAsc(key)} className={`${styles.icon} ${sortConfig.isActive && sortConfig.key === key && sortConfig.direction === 'asc' ? styles.activeIcon : ''}`}  />
                                        <IoCaretDownSharp onClick={() => handleDesc(key)} className={`${styles.icon} ${sortConfig.isActive && sortConfig.key === key && sortConfig.direction === 'desc' ? styles.activeIcon : ''}`} />
                                    </div>
                                </div>
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody className={styles.tbody}>
                    {sortedTable.map((row, rowIndex) => (
                        <tr key={`b-tr-${rowIndex}`} className={styles.tr}>
                            {Object.entries(row).map(([key, value], colIndex) => (
                                <td key={colIndex}
                                    className={styles.td}
                                    style={{ textAlign: isNumber(key) ? 'right' : 'left', borderBottomLeftRadius: rowIndex === sortedTable.length - 1 && colIndex === 0 ? '10px' : '0', borderBottomRightRadius: rowIndex === sortedTable.length - 1 && colIndex === Object.values(row).length - 1 ? '10px' : '0' }}>
                                    {isNumber(key) ? formatNumberWithComma(value) : value}
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>}
        </div>
    );
}
